import React, { useState } from 'react';
import { Mail, MapPin, Send, Loader2, Linkedin, Instagram, Youtube, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { contactAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('contact');
  
  // Dynamic page title
  usePageTitle('nav.contact', 'Contact', 'İletişim');

  // Social media links
  const socialLinks = [
    { 
      name: 'LinkedIn', 
      url: 'https://www.linkedin.com/company/kartal-air/', 
      icon: Linkedin,
      color: '#0077B5',
      descKey: 'contact.social.linkedinDesc'
    },
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/kartalairoffical?igshid=ODVuZW1oNmxjcmdy', 
      icon: Instagram,
      color: '#E4405F',
      descKey: 'contact.social.instagramDesc'
    },
    { 
      name: 'YouTube', 
      url: 'https://www.youtube.com/@kartalairiha', 
      icon: Youtube,
      color: '#FF0000',
      descKey: 'contact.social.youtubeDesc'
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await contactAPI.submit(formData);
      toast.success(t('contact.form.success'), { description: t('contact.form.successDesc') });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(t('contact.form.error'), { description: err.message || t('contact.form.errorDesc') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 px-[7.6923%] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(0, 240, 255, 0.2) 100%)'
          }}></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto text-center">
          <h1 className="display-huge mb-6" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.5)'
          }}>{getContent('hero_title', t, 'contact.hero.title', language)}</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_subtitle', t, 'contact.hero.description', language)}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="glass-card p-8">
              <h2 className="heading-1 mb-6" style={{ color: '#F8FAFC' }}>
                {getContent('form_title', t, 'contact.form.title', language)}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block body-medium mb-2" style={{ color: '#F8FAFC' }}>{t('contact.form.name')} *</label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange}
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 text-white body-medium focus:outline-none transition-all duration-300 rounded-lg"
                    style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 240, 255, 0.3)', color: '#F8FAFC' }}
                    placeholder={t('contact.form.namePlaceholder')} />
                </div>

                <div>
                  <label htmlFor="email" className="block body-medium text-white mb-2">{t('contact.form.email')} *</label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange}
                    data-testid="contact-email-input"
                    className="w-full px-4 py-3 text-white body-medium focus:outline-none transition-all duration-300 rounded-lg"
                    style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 240, 255, 0.3)', color: '#F8FAFC' }}
                    placeholder={t('contact.form.emailPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="subject" className="block body-medium text-white mb-2">{t('contact.form.subject')} *</label>
                  <input type="text" id="subject" name="subject" required value={formData.subject} onChange={handleChange}
                    data-testid="contact-subject-input"
                    className="w-full px-4 py-3 text-white body-medium focus:outline-none transition-all duration-300 rounded-lg"
                    style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 240, 255, 0.3)', color: '#F8FAFC' }}
                    placeholder={t('contact.form.subjectPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="message" className="block body-medium text-white mb-2">{t('contact.form.message')} *</label>
                  <textarea id="message" name="message" required rows="6" value={formData.message} onChange={handleChange}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 text-white body-medium focus:outline-none transition-all duration-300 resize-none rounded-lg"
                    style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 240, 255, 0.3)', color: '#F8FAFC' }}
                    placeholder={t('contact.form.messagePlaceholder')}></textarea>
                </div>

                <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn"
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />{t('contact.form.sending')}</>) : (<>{t('contact.form.submit')}<Send size={20} /></>)}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Location & Email */}
              <div className="glass-card p-8">
                <h2 className="heading-1 mb-6" style={{ color: '#F8FAFC' }}>
                  {getContent('info_title', t, 'contact.info.title', language)}
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <MapPin size={24} className="flex-shrink-0 mt-1" style={{ color: '#00F0FF' }} />
                    <div>
                      <h3 className="body-medium font-semibold mb-1" style={{ color: '#F8FAFC' }}>{t('contact.info.address')}</h3>
                      <p className="body-small whitespace-pre-line" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                        {getContent('address', t, 'contact.info.schoolAddress', language)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Mail size={24} className="flex-shrink-0 mt-1" style={{ color: '#00F0FF' }} />
                    <div>
                      <h3 className="body-medium font-semibold mb-1" style={{ color: '#F8FAFC' }}>{t('contact.info.email')}</h3>
                      <a 
                        href="mailto:kartalair.sponsorluk@gmail.com"
                        className="body-small hover:text-[#00F0FF] transition-colors duration-300"
                        style={{ color: '#F8FAFC', opacity: 0.85 }}
                      >
                        kartalair.sponsorluk@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Sponsorship Button */}
                <a 
                  href="mailto:kartalair.sponsorluk@gmail.com?subject=Sponsorluk%20Talebi"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
                    color: '#0F172A',
                    boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.6)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  data-testid="sponsorship-email-btn"
                >
                  <Mail size={22} />
                  {t('contact.sponsorship.button')}
                </a>
              </div>

              {/* Social Media */}
              <div className="glass-card p-8">
                <h2 className="heading-1 mb-6" style={{ color: '#F8FAFC' }}>
                  {getContent('social_title', t, 'contact.info.socialMedia', language)}
                </h2>
                <p className="body-medium mb-6" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  {getContent('social_text', t, 'contact.info.followUs', language)}
                </p>

                <div className="space-y-4">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a 
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 rounded-lg transition-all duration-300"
                        style={{
                          background: 'rgba(15, 23, 42, 0.6)',
                          border: '1px solid rgba(0, 240, 255, 0.2)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.borderColor = social.color;
                          e.currentTarget.style.boxShadow = `0 0 20px ${social.color}40`;
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.2)';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                        data-testid={`social-${social.name.toLowerCase()}`}
                      >
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ background: `${social.color}20` }}
                        >
                          <Icon size={24} style={{ color: social.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold" style={{ color: '#F8FAFC' }}>{social.name}</h3>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>{t(social.descKey)}</p>
                        </div>
                        <ExternalLink size={18} style={{ color: '#64748B' }} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-[7.6923%]" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-12 text-center" style={{ color: '#F8FAFC' }}>
            {getContent('faq_title', t, 'contact.faq.title', language)}
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="glass-card p-6 dark-transition dark-hover">
              <h3 className="heading-3 mb-3" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {getContent('faq1_question', t, 'contact.faq.q1', language)}
              </h3>
              <p className="body-medium" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                {getContent('faq1_answer', t, 'contact.faq.a1', language)}
              </p>
            </div>
            <div className="glass-card p-6 dark-transition dark-hover">
              <h3 className="heading-3 mb-3" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {getContent('faq2_question', t, 'contact.faq.q2', language)}
              </h3>
              <p className="body-medium" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                {getContent('faq2_answer', t, 'contact.faq.a2', language)}
              </p>
            </div>
            <div className="glass-card p-6 dark-transition dark-hover">
              <h3 className="heading-3 mb-3" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {getContent('faq3_question', t, 'contact.faq.q3', language)}
              </h3>
              <p className="body-medium" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                {getContent('faq3_answer', t, 'contact.faq.a3', language)}
              </p>
            </div>
            <div className="glass-card p-6 dark-transition dark-hover">
              <h3 className="heading-3 mb-3" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {getContent('faq4_question', t, 'contact.faq.q4', language)}
              </h3>
              <p className="body-medium" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                {getContent('faq4_answer', t, 'contact.faq.a4', language)}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
